<template>
  <top-nav />
  <div class="max-w-6xl px-4 py-5 mx-auto sm:mt-0 sm:py-10 sm:px-8 md:px-12">
    <div class="mt-3 sm:mt-0">
      <div v-if="!reviews.length && !loadingReviewData">
        <p class="text-center text-gray-500 dark:text-gray-300">No reviews yet.</p>
      </div>

      <div v-else-if="!loadingReviewData">
        <h3 class="text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Streaks: {{ streak }}</h3>

        <!-- Streak levels -->
        <div class="w-full p-4 my-5 overflow-scroll bg-white rounded-lg dark:bg-gray-900">
          <div class="flex items-start justify-between space-x-5 sm:space-x-8">
            <template v-for="level in levels" :key="level">
              <div
                class="flex-none block text-xs text-center text-gray-900 dark:text-white sm:px-4 sm:text-sm"
                :class="{
                  'opacity-5 dark:opacity-20': currentLevel.level < level.level,
                }"
              >
                <div class="w-10 h-10 mx-auto sm:h-20 sm:w-20">
                  <img :src="require(`../assets/badges/${level.badge}`)" :class="{ 'scale-110 ': currentLevel.level == level.level }" />
                </div>
                <div class="font-semibold">{{ level.name }}</div>
                <div>Streaks: {{ `${level.streak.min}-${level.streak.max}` }}</div>
              </div>
            </template>
          </div>
        </div>

        <!-- Annual view -->
        <div class="my-16">
          <h3 class="text-lg font-bold leading-6 text-gray-900 capitalize dark:text-gray-50 sm:text-xl">Daily Reviews</h3>
          <div class="flex w-full p-4 pt-5 mt-5 overflow-scroll bg-white rounded-lg dark:bg-gray-900">
            <div class="mt-2 mr-2 grid grid-flow-col grid-rows-8 space-y-0.5 text-center text-xs text-gray-500 dark:text-gray-300 sm:mt-1 sm:space-y-1">
              <div>&#8203;</div>
              <div>M</div>
              <div>T</div>
              <div>W</div>
              <div>T</div>
              <div>F</div>
              <div>S</div>
              <div>S</div>
            </div>
            <div>
              <div class="grid w-full grid-cols-12 mb-2 text-sm text-center text-gray-500 dark:text-gray-300">
                <template v-for="month in months" :key="month">
                  <!-- <div :ref="month.toLocaleString('en-US', { month: 'long' }) == currentMonthName() ?? 'currentMonth'"> -->
                  <div>
                    <div>{{ month.toLocaleString("en-US", { month: "short" }) }}</div>
                  </div>
                </template>
              </div>
              <div class="grid grid-flow-col text-xs text-center grid-rows-7">
                <template v-for="day in days" :key="day">
                  <div class="relative group">
                    <div
                      class="m-0.5 block h-5 w-5 rounded border sm:h-6 sm:w-6"
                      :class="{
                        'border-green-200 bg-green-200 dark:bg-green-900 dark:border-green-900': !reviewEval(day, reviews).reviewId && day > days[0].getTime() && day < new Date().getTime(), //No review due that day
                        'border-red-500 bg-red-500': reviewEval(day, reviews).missed && !sameDay(day.getTime() / 1000, new Date().getTime() / 1000), // Missed
                        'border-blue-500 bg-blue-500': !reviewEval(day, reviews).completed && reviewEval(day, reviews).reviewId && sameDay(day.getTime() / 1000, new Date().getTime() / 1000), // due today
                        'border-yellow-500 bg-yellow-500': !reviewEval(day, reviews).completed && sameDay(day.getTime() / 1000, user.metadata.createdAt), // user's first day
                        'border-green-500 bg-green-500': reviewEval(day, reviews).completed, // Completed
                      }"
                    ></div>
                    <div class="absolute bottom-0 flex-col items-center hidden mb-4 -left-2/3 group-hover:flex">
                      <span class="relative z-10 flex p-2 text-xs leading-none text-white whitespace-no-wrap bg-black rounded shadow-lg w-fit whitespace-nowrap">
                        <div>
                          {{
                            day.toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })
                          }}
                          <!-- Link to missed review -->
                          <div
                            v-if="
                              (reviewEval(day, reviews).missed && !sameDay(day.getTime() / 1000, new Date().getTime() / 1000)) || //missed
                              (!reviewEval(day, reviews).completed && reviewEval(day, reviews).reviewId && sameDay(day.getTime() / 1000, new Date().getTime() / 1000)) // due today
                            "
                            class="mt-1 text-blue-500 flex-0"
                          >
                            <router-link :to="`/?review=${reviewEval(day, reviews).reviewId}`">Review now</router-link>
                          </div>
                        </div>
                      </span>
                      <div class="w-3 h-3 -mt-2 rotate-45 bg-black"></div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <!-- Legend -->
          <div class="block mt-2 space-y-2 text-xs text-gray-500 sm:item-center sm:flex sm:space-y-0 sm:space-x-5">
            <div class="flex items-center space-x-1">
              <div class="flex-none w-3 h-3 bg-green-200 rounded dark:bg-green-900"></div>
              <div>No review due</div>
            </div>
            <div class="flex items-center space-x-1">
              <div class="flex-none w-3 h-3 bg-green-500 rounded"></div>
              <div>Completed</div>
            </div>
            <div class="flex items-center space-x-1">
              <div class="flex-none w-3 h-3 bg-red-500 rounded"></div>
              <div>Missed</div>
            </div>
            <div class="flex items-center space-x-1">
              <div class="flex-none w-3 h-3 bg-blue-500 rounded"></div>
              <div>Due today</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { db } from "../firebase";
import { collection, getDocs, where, query, orderBy } from "firebase/firestore";
import utilFunctions from "../composables/util";
import firebaseUser from "../composables/userAuth";
import { ref, watchEffect } from "vue";
import { useRoute } from "vue-router";
import TopNav from "../components/nav/TopNav.vue";
import levels from "../composables/levels.json";
import "animate.css";

export default {
  components: {
    TopNav,
  },
  setup() {
    const { user } = firebaseUser();
    const { dateBounds, sameDay } = utilFunctions();
    const route = useRoute();
    const reviews = ref([]);
    const streak = ref(0);
    const currentLevel = ref({});
    const loadingReviewData = ref(true);
    const days = ref([]);
    const months = ref([]);

    watchEffect(async () => {
      if (user.value) {
        const reviewRef = collection(db, "users", user.value.uid, "reviews");
        const reviewQuery = query(reviewRef, where("due", "<=", dateBounds(new Date()).endUtc), orderBy("due", "asc"));

        const reviewData = await getDocs(reviewQuery);

        if (!reviewData.empty) {
          reviewData.forEach((doc) => {
            const review = doc.data();
            reviews.value.push({ ...review, id: doc.id });
            if (sameDay(review.due.seconds, review.completedAt?.seconds)) {
              // review due and completed on same day
              streak.value++;
            } else if (!sameDay(review.due?.seconds, new Date().getTime() / 1000)) {
              // review not due today
              streak.value = 0;
            }
          });
        }

        loadingReviewData.value = false;

        currentLevel.value = levels.find((level) => {
          return streak.value >= level.streak.min && streak.value <= level.streak.max;
        });

        // get min and max streaks for next user level
        // nextLevel.value = levels.find((level) => {
        //   return level.level === currentLevel.value.level + 1;
        // });

        days.value = Array.from(Array(365).keys()).map((day) => new Date(Date.UTC(2022, 0, day + 1)));

        // add last five days of previous year to beginning annual array
        for (let i = 0; i < 5; i++) {
          days.value.unshift(new Date(2021, 11, 31 - i));
        }

        // create array for 12 months, each item includes the date object with the first day of the month
        months.value = Array.from(Array(12).keys()).map((month) => new Date(Date.UTC(2022, month, 1)));
      }
    });

    function currentMonthName() {
      var month = new Date().getMonth();
      var months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      return months[month];
    }

    function reviewEval(dayString, reviews) {
      const timestamp = new Date(dayString).getTime() / 1000;
      const matchedReview = reviews.find((review) => sameDay(review?.due?.seconds, timestamp));

      const result = {
        completed: null,
        missed: null,
        reviewId: null,
      };

      if (matchedReview) {
        result.completed = matchedReview.completedAt;
        result.missed = sameDay(timestamp, matchedReview.due.seconds) && !matchedReview.completedAt;
        result.reviewId = matchedReview.id;
      }

      return result;
    }

    return {
      route,
      reviews,
      sameDay,
      streak,
      currentLevel,
      months,
      reviewEval,
      loadingReviewData,
      currentMonthName,
      levels,
      days,
      user,
    };
  },
};
</script>
